import React from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import Avatar from 'react-avatar';
import { Icon, Container } from 'semantic-ui-react';
import { Media } from '../../../../mediaSetting';
import { getUserAvatarText } from 'businessLogic/userAvatar';
import { sortUsersIcanManage } from 'businessLogic/sorting';
import { convertToLocalMoment } from 'businessLogic/datetime';
import { updateSelectedUser } from 'actions/meetingRoom.js';
import MeetingRoomUserActionsMenu from 'containers/MeetingRoomManageUsersActionMenu';

import { showHideMenu } from 'components/shared/contextualMenu/content';
import moment from 'moment';

const MeetingRoomManageUsersCenterPanel = ({
  usersIcanManage,
  searchTerm,
  onSearchTermInputChange,
  fetchingUserICanManage,
  timezone,
  selectedMeetingRoom,
  isOnMobileOrTablet,
}) => {
  const dispatch = useDispatch();
  const getUserNameText = (user) => {
    if (user.isEmailOnlyUser) return user.email;

    return user.firstName + ' ' + user.lastName;
  };

  const getUserRolesText = (user) => {
    let emailOnlyUserRoles = '';
    let emailOnlyUserCreateUTC = [];
    let roleArray = user.role.split(',');

    roleArray.map((singleRole, i) => {
      if (i === 0) {
        emailOnlyUserRoles = singleRole
          .split('_')[0]
          .replace(/Meeting room/gi, '')
          .trim();
      } else {
        emailOnlyUserRoles =
          emailOnlyUserRoles +
          ', ' +
          singleRole
            .split('_')[0]
            .replace(/Meeting room/gi, '')
            .trim();
      }
      emailOnlyUserCreateUTC.push(singleRole.split('_')[1].trim());
    });

    const rolesText = emailOnlyUserRoles;

    if (!user.isEmailOnlyUser) return rolesText;

    const latestUtcTime = emailOnlyUserCreateUTC.reduce((createdDateTimeUtcA, createdDateTimeUtcB) => {
      let momentA = moment(createdDateTimeUtcA);
      let momentB = moment(createdDateTimeUtcB);
      return momentA > momentB ? createdDateTimeUtcA : createdDateTimeUtcB;
    });

    let invitedOnLatestLocalTimeText = '';
    if (timezone == 0 || !!timezone) {
      const invitedOnLatestLocalTime =
        timezone == 0 || !!timezone ? convertToLocalMoment(moment(latestUtcTime), timezone) : '';
      const invitedOnLatestLocalFormatedDate = invitedOnLatestLocalTime.format(APP.LANGUAGE.MainReport.DateFormat);
      const momentToday = moment().utcOffset(timezone);

      if (momentToday.format(APP.LANGUAGE.MainReport.DateFormat) === invitedOnLatestLocalFormatedDate) {
        invitedOnLatestLocalTimeText = 'today';
      } else if (
        momentToday.subtract(1, 'days').format(APP.LANGUAGE.MainReport.DateFormat) === invitedOnLatestLocalFormatedDate
      ) {
        invitedOnLatestLocalTimeText = 'yesterday';
      } else {
        invitedOnLatestLocalTimeText = 'on ' + invitedOnLatestLocalFormatedDate;
      }
    }
    return `Invited ${invitedOnLatestLocalTimeText} as ${rolesText}`;
  };

  const getUserRowMarkup = (user, isGray) => {
    let avatarText = getUserAvatarText(user);
    let userNameText = getUserNameText(user);
    let userRolesText = getUserRolesText(user);
    let userNameMarkup = '';
    let userRolesMarkup = '';
    const avatarSizeMobile = 50;
    const avatarSizeTabletComputer = 33;

    if (user.isEmailOnlyUser) {
      userNameMarkup = <div className="trim-long-text">{userNameText}</div>;
      userRolesMarkup = <i>{userRolesText}</i>;
    } else {
      userNameMarkup = <div className="trim-long-text">{userNameText}</div>;
      userRolesMarkup = userRolesText;
    }

    return (
      <div
        className="row flexible row-hoverable"
        data-opaque={!!isGray}
        key={user.id}
        onClick={(event) => {
          showHideMenu(null, '.manage-users-meeting-room-container', user.id, event.target, event.currentTarget);
          dispatch(updateSelectedUser(user.id));
        }}
      >
        {/* Column #1 - Avatar + User name (7 wide for tablet and above / full 16 wide on mobile) */}
        <div className="tablet only computer only seven wide column left-column-fixed status-column left-text users-column inline-flex center-flex">
          <div className="fl" style={{ height: avatarSizeTabletComputer + 'px' }}>
            <Avatar
              round={true}
              size={avatarSizeTabletComputer}
              src={user.userImageUrl}
              name={avatarText}
              className="user-avatar"
              color={user.isEmailOnlyUser ? window.styleVariable.primaryHoverColour : ''}
              textSizeRatio={2.5}
            />
          </div>
          <div className="pdls max-width pdrs">{userNameMarkup}</div>
          <div>
            <Icon
              name="angle down"
              style={{ minWidth: '20px' }}
              className={isOnMobileOrTablet ? 'relative arrow-down-on-mobile' : 'mrrxs relative arrow-down-on-mobile'}
            />
          </div>
        </div>

        {/* Column #2 - User roles - (shown only on tablet and above) */}
        <div className="tablet only computer only nine wide column right-column-fixed left-text users-column inline-flex center-flex pdlnull">
          <div>{userRolesMarkup}</div>
        </div>
        <div
          className="mobile only column pdlnull pdrnull left-text inline-flex center-flex"
          style={{ width: 'calc(100% - 20px)' }}
        >
          <Avatar
            round={true}
            size={avatarSizeMobile}
            src={user.userImageUrl}
            name={avatarText}
            className="user-avatar"
            color={user.isEmailOnlyUser ? window.styleVariable.primaryHoverColour : ''}
            textSizeRatio={2}
          />
          <div className="mrlm" style={{ width: 'calc(100% - 75px)' }}>
            {userNameMarkup}
            <div className="text-size-ms secondary-text">{userRolesMarkup}</div>
          </div>
        </div>
        <Container fluid as={Media} at="mobile" className="inline-flex center-with-flex" style={{ width: '20px' }}>
          <Icon name="angle down" style={{ minWidth: '20px' }} className="mrrnull relative arrow-down-on-mobile" />
        </Container>
        <MeetingRoomUserActionsMenu selectedUser={user} selectedMeetingRoom={selectedMeetingRoom} dispatch={dispatch} />
      </div>
    );
  };

  const tableHeaderMarkup = (
    <div className="row flexible  color-meeting-room-bg pdtnull pdbnull header-row pdlnull">
      {/* Column #1 header */}
      <span className="ui seven wide tablet  seven wide computer  sixteen wide mobile  column  icon input color-white display-flex">
        <input
          type="text"
          className="ui color-white input color-meeting-room-bg-highlight max-width pdxs"
          onChange={(e) => onSearchTermInputChange(e)}
          value={searchTerm}
          placeholder={APP.LANGUAGE.MeetingRoomManageUsers.ColumnHeader1Title}
          name="mr-searchTerm"
          autoComplete="not-enable"
        />
        <i className="filter icon mrrml" />
      </span>

      {/* Column #2 header */}
      <span className="ui  tablet only  computer only  nine wide column color-white display-flex pdlnull">
        <div className="color-white max-width pdxs pdlnull left-text">
          {APP.LANGUAGE.MeetingRoomManageUsers.ColumnHeader2Title}
        </div>
      </span>
    </div>
  );

  const usersLoadingMarkup = [{}, {}].map((o, i) => {
    return (
      <div className="row flexible row-hoverable " key={i}>
        {i == 0 ? (
          <div className="ui active inverted dimmer ">
            <div className="ui text loader pdtxl mrtl " />
          </div>
        ) : (
          <div className="ui active inverted dimmer " />
        )}

        <div className="seven wide tablet  seven wide computer  sixteen wide mobile  column left-column-fixed status-column left-text users-column">
          <span className="color-gray-bg color-gray ">asdasdasdasdasd</span>
        </div>
        <div className="ui  tablet only  computer only  nine wide column color-white display-flex">
          <span className="color-gray-bg color-gray ">asdasdasdasdasd</span>
        </div>
      </div>
    );
  });

  const usersNoAccessMarkup = (
    <div className="row flexible row-hoverable ">
      <div className="seven wide tablet  seven wide computer  sixteen wide mobile">
        <div className="column text-size-ml">{APP.LANGUAGE.MeetingRoomManageUsers.NoMemberAvailable}</div>
      </div>
    </div>
  );

  const sortedUsers = sortUsersIcanManage(usersIcanManage);
  let usersMatchingSearchTerm = sortedUsers;
  let usersNotMatchingSearchTerm = [];

  if (searchTerm && searchTerm.trim().length > 0) {
    // do the filtering
    const searchTermLowerCase = searchTerm.toLowerCase();
    usersMatchingSearchTerm = [];
    usersNotMatchingSearchTerm = [];

    sortedUsers.forEach((user) => {
      if (
        getUserNameText(user).toLowerCase().indexOf(searchTermLowerCase) > -1 ||
        getUserRolesText(user).toLowerCase().indexOf(searchTermLowerCase) > -1
      )
        usersMatchingSearchTerm.push(user);
      else usersNotMatchingSearchTerm.push(user);
    });
  }

  const usersMatchingSearchTermMarkup = usersMatchingSearchTerm
    ? usersMatchingSearchTerm.map((user) => getUserRowMarkup(user, false))
    : '';
  const usersNotMatchingSearchTermMarkup = usersNotMatchingSearchTerm
    ? usersNotMatchingSearchTerm.map((user) => getUserRowMarkup(user, true))
    : '';

  let tableContentMarkup;
  if (fetchingUserICanManage === true) tableContentMarkup = usersLoadingMarkup;
  else if (usersIcanManage && usersIcanManage.length > 0)
    tableContentMarkup = usersMatchingSearchTermMarkup.concat(usersNotMatchingSearchTermMarkup);
  else tableContentMarkup = usersNoAccessMarkup;

  return (
    <span>
      <div className="ui center aligned padded grid">
        {tableHeaderMarkup}
        {tableContentMarkup}
      </div>
    </span>
  );
};

MeetingRoomManageUsersCenterPanel.propTypes = {
  usersIcanManage: PropTypes.array.isRequired,
  searchTerm: PropTypes.string,
};

export default MeetingRoomManageUsersCenterPanel;
